import {BreakpointObserver, BreakpointState} from '@angular/cdk/layout';
import {CdkPortalOutlet} from '@angular/cdk/portal';
import {CommonModule} from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  HostBinding,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MatSuffix} from '@angular/material/form-field';
import {MatSidenav, MatSidenavContainer, MatSidenavContent} from '@angular/material/sidenav';
import {ActivatedRoute, RouterOutlet} from '@angular/router';
import {CovIconComponent, IconName} from '@cov/ui-angular';
import {TranslocoModule} from '@jsverse/transloco';
import {combineLatest, filter, map, Subject, takeUntil} from 'rxjs';
import {InquiryService} from '../../features/inquiry/service/inquiry.service';
import {FloatingInquiryButtonComponent} from '../../features/inquiry/ui/floating-inquiry-button/floating-inquiry-button.component';
import {SearchBarComponent} from '../../features/search/ui/search-bar/search-bar.component';
import {BREAKPOINTS_QUERIES, SIDEBAR_ANIMATION_TIME} from '../../shared/constants';
import {SessionState} from '../../shared/enum/session-state.enum';
import {AgGridService} from '../../shared/services/ag-grid.service';
import {AsmService} from '../../shared/services/asm.service';
import {PageService} from '../../shared/services/page.service';
import {SessionService} from '../../shared/services/session.service';
import {SidePanelService} from '../../shared/services/side-panel.service';
import {SoldToShipToService} from '../../shared/services/sold-to-ship-to.service';
import {UserService} from '../../shared/services/user.service';
import {FooterComponent} from '../../shared/ui/footer/footer.component';
import {LogoComponent} from '../../shared/ui/logo/logo.component';
import {MobileNavigationComponent} from '../../shared/ui/mobile-navigation/mobile-navigation.component';
import {SidebarNavigationComponent} from '../../shared/ui/sidebar-navigation/sidebar-navigation.component';
import {TopBarComponent} from '../../shared/ui/top-bar/top-bar.component';

@Component({
  selector: 'cov-main-application',
  imports: [
    CommonModule,
    RouterOutlet,
    SidebarNavigationComponent,
    TopBarComponent,
    FooterComponent,
    LogoComponent,
    CovIconComponent,
    TranslocoModule,
    MobileNavigationComponent,
    MatSidenavContainer,
    MatSidenav,
    MatSidenavContent,
    CdkPortalOutlet,
    MatSuffix,
    FloatingInquiryButtonComponent,
    SearchBarComponent,
  ],
  templateUrl: './main-application.component.html',
  styleUrl: './main-application.component.scss',
})
export class MainApplicationComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class.cov-main-application-view-rendered') addClass = false;
  @ViewChild(CdkPortalOutlet) outlet: CdkPortalOutlet | undefined;
  @ViewChild(MatSidenav) sideNavRef: MatSidenav | undefined;

  // INJECT
  protected pageService = inject(PageService);
  protected sidepanelService = inject(SidePanelService);
  protected inquiryService = inject(InquiryService);
  protected asmService = inject(AsmService);
  protected userService = inject(UserService);
  protected agGridService = inject(AgGridService);
  private breakpointObserver = inject(BreakpointObserver);
  private soldToShipToService = inject(SoldToShipToService);
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private sessionService = inject(SessionService);

  // CONST
  protected readonly IconName = IconName;
  protected iconName = IconName;
  public pageTitle$ = this.pageService.getCurrentPageTitle();
  private destroy$ = new Subject<void>();

  // STATE
  protected impersonateUser$ = this.asmService.getImpersonateUser();
  protected currentUser$ = this.userService.getCurrentUser();
  protected currentUserOrImpersonateUser$ = combineLatest([
    this.impersonateUser$,
    this.currentUser$,
  ]).pipe(map(([impersonateUser, currentUser]) => impersonateUser || currentUser));

  protected viewRendered = signal(false);
  protected disableTopBar = signal(false);
  public navigationExpanded = signal(true);

  // METHODS
  public ngOnInit() {
    this.breakpointObserver
      .observe([BREAKPOINTS_QUERIES.lg])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state: BreakpointState) => {
        if (state.matches) {
          this.navigationExpanded.set(true);
        }
      });

    this.sessionService.sessionStates$
      .pipe(
        filter(
          (states) =>
            states.includes(SessionState.CURRENTUSER) ||
            states.includes(SessionState.IMPERSONATESESSION),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.soldToShipToService.init();
      });

    this.route.data.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.disableTopBar.set(!!data['disableTopBar']);
    });
  }

  public onNavigationExpandButtonClick(): void {
    this.navigationExpanded.set(!this.navigationExpanded());
    setTimeout(() => {
      this.agGridService.autoSize();
    }, SIDEBAR_ANIMATION_TIME + SIDEBAR_ANIMATION_TIME); // Ensure the grid resizes after the sidebar animation completes, re-think switch to event based approach
  }

  public openInquirySideDrawer(): void {
    this.inquiryService.open();
  }

  public ngAfterViewInit() {
    if (this.outlet) {
      this.sidepanelService.setPortalOutlet(this.outlet);
    }
    if (this.sideNavRef) {
      this.sidepanelService.setSideNavRef(this.sideNavRef);
    }

    setTimeout(() => {
      this.viewRendered.set(true);
    }, SIDEBAR_ANIMATION_TIME); // same as --application-global-animation-duration CSS variable
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
